<script setup lang="ts">
import ConfirmDlg from '@/components/confirm-dlg.vue'
import { useToast } from '@/composables/toast'
import type { ItemSlotBase } from 'vuetify/lib/components/VDataTable/types.mjs'
import { ShWappenSvg, TgWappenSvg } from '@/helpers/images'
import { errToStr } from '@/helpers/tools'
import { News, NewsCollection } from '@/models/News'
import { onMounted, ref, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import type { DataTableHeader } from 'vuetify'
import dayjs from 'dayjs'
import KpToolbar from '@/components/kp-toolbar.vue'

const { t } = useI18n()
const news = new NewsCollection()
const newsToDestroy: Ref<News | null> = ref(null)
const confirmDelDlg = ref(false)
const newsLoading = ref(true)
const headers: DataTableHeader[] = [
    { value: 'kanton', align: 'start', width: '120px' },
    { title: t('title'), value: 'titel', sortable: true, width: '300px', nowrap: false },
    { title: t('erstellt_von'), value: 'ersteller_name', width: '200px', sortable: true },
    { title: t('show_from'), value: 'anzeige_von', width: '160px', sortable: true },
    { title: t('show_until'), value: 'anzeige_bis', width: '160px', sortable: true },
    { value: 'actions', align: 'start', width: '120px', sortable: false },
]

const toast = useToast()
const router = useRouter()
const route = useRoute()

onMounted(() => {
    getNews()
})

async function getNews() {
    newsLoading.value = true
    try {
        await news.query()
    } catch (res) {
        toast.add({ text: 'Fehler: ' + res, color: 'error' })
    } finally {
        newsLoading.value = false
    }
}

function showForm(item: News) {
    router.push({ name: 'news_edit', params: { id: item.id } })
}

function onAdd() {
    router.push({ name: 'news_edit', params: { id: 'new' } })
}

function destroy(news: News) {
    newsToDestroy.value = news
    confirmDelDlg.value = true
}

async function confirmDestroy() {
    if (newsToDestroy.value) {
        try {
            await newsToDestroy.value.destroy()
            getNews()
        } catch (e) {
            toast.add({ text: errToStr(e), color: 'error' })
        }
    }
    confirmDelDlg.value = false
    newsToDestroy.value = null
}

function onRowClicked(event: PointerEvent, row: ItemSlotBase<News>) {
    showForm(row.item)
}
</script>

<template>
    <div v-if="route.name === 'news'">
        <KpToolbar icon="fas fa-rss" :title="t('news')">
            <template #controls>
                <v-btn
                    class="hidden-sm-and-up"
                    rounded="xl"
                    variant="flat"
                    color="subtleHint"
                    @click="onAdd"
                    icon="fas fa-plus"
                >
                </v-btn>
                <v-btn
                    class="hidden-xs"
                    rounded="xl"
                    variant="flat"
                    color="subtleHint"
                    @click="onAdd"
                    prepend-icon="fas fa-plus"
                >
                    {{ t('news_add') }}
                </v-btn>
            </template>
        </KpToolbar>
        <v-container fluid class="kp-large-container">
            <v-data-table-server
                class="fixed-table row-pointer"
                :headers="headers"
                :items-length="news.getServerTotal()"
                :items="news ? news.getModels() : []"
                item-key="id"
                :loading="newsLoading"
                :no-data-text="t('no_records')"
                disable-pagination
                hide-default-footer
                @click:row="onRowClicked"
            >
                <template v-slot:[`item.kanton`]="{ item }">
                    <div style="display: flex">
                        <v-img
                            v-if="item.kanton?.includes('tg')"
                            :src="TgWappenSvg"
                            max-width="30px"
                            class="ma-1"
                        />
                        <v-img
                            v-if="item.kanton?.includes('sh')"
                            :src="ShWappenSvg"
                            max-width="30px"
                            class="ma-1"
                        />
                    </div>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <span>
                        <v-btn
                            class="mr-1"
                            size="x-small"
                            variant="text"
                            color="accent"
                            icon="fas fa-pen"
                            @click.stop="showForm(item)"
                        >
                        </v-btn>
                        <v-btn
                            class="mr-1"
                            size="x-small"
                            variant="text"
                            color="error"
                            icon="fas fa-trash"
                            @click.stop="destroy(item)"
                        >
                        </v-btn>
                    </span>
                </template>
                <template v-slot:[`item.titel`]="{ item }">
                    <strong>{{ item.titel }}</strong>
                </template>
                <template v-slot:[`item.anzeige_von`]="{ item }">
                    <span>{{
                        item.statische_anzeige ? '-' : dayjs(item.anzeige_von).format('DD.MM.YYYY')
                    }}</span>
                </template>
                <template v-slot:[`item.anzeige_bis`]="{ item }">
                    <span>{{
                        item.statische_anzeige ? '-' : dayjs(item.anzeige_bis).format('DD.MM.YYYY')
                    }}</span>
                </template>
            </v-data-table-server>
            <ConfirmDlg
                v-model="confirmDelDlg"
                :title="t('bestaetigung')"
                :text="t('really_delete_rec')"
                @yes="confirmDestroy"
                @no="newsToDestroy = null"
            />
        </v-container>
    </div>
    <router-view v-else />
</template>

<style lang="scss" scoped>
.small-icon {
    font-size: 15px;
}
.fixed-table {
    :deep(table) {
        table-layout: fixed;
        width: 100%;
    }
}
.row-pointer {
    :deep(tbody tr :hover) {
        cursor: pointer;
    }
}
@media screen and (min-width: 600px) {
    .fixed-table {
        :deep(table td) {
            text-overflow: ellipsis;
        }
    }
    .fixed-overflow-table {
        :deep(table td) {
            overflow: hidden;
        }
    }
}
@media screen and (max-width: 600px) {
    .fixed-table {
        :deep(table th) {
            width: 100%;
        }
    }
}
</style>
